import { RotateCcw } from 'lucide-react';
import { Card, CardHeader, CardBody } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { DateRangePicker } from '@/components/ui/DateRangePicker';
import { PatientSelector } from './PatientSelector';
import { ActivityFilters } from './ActivityFilters';
import { SelectionPreview } from './SelectionPreview';
import { SelectionSummary } from './SelectionSummary';
import { useDoctorDownloadController } from './useDoctorDownloadController';
import type { DownloadMode } from './downloadWorkflow';

interface DoctorDownloadScreenProps {
  mode: DownloadMode;
}

export function DoctorDownloadScreen({ mode }: DoctorDownloadScreenProps) {
  const controller = useDoctorDownloadController(mode);
  const isDownloading =
    controller.downloadProgress.status === 'fetching' ||
    controller.downloadProgress.status === 'packaging';

  if (controller.isLoadingPatients) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader
          title="Build Your Selection"
          description={controller.config.cardDescription}
          action={
            controller.hasSelection && (
              <Button
                variant="ghost"
                size="sm"
                onClick={controller.clearFilters}
                leftIcon={<RotateCcw className="h-4 w-4" />}
              >
                Clear Filters
              </Button>
            )
          }
        />
        <CardBody className="space-y-6">
          <div className="grid gap-4 md:grid-cols-2">
            <PatientSelector
              patients={controller.allPatients}
              selectedIds={controller.query.patientIds}
              onChange={controller.setPatients}
              isLoading={controller.isLoadingPatients}
            />
            <div className="space-y-2">
              <label className="block text-sm font-medium text-foreground">
                Date Range
              </label>
              <DateRangePicker
                from={controller.query.dateRange.from}
                to={controller.query.dateRange.to}
                onChange={controller.setDateRange}
              />
            </div>
          </div>

          <ActivityFilters
            activityTypes={controller.query.activityTypes}
            vibrationModes={controller.query.vibrationModes}
            onActivityTypesChange={controller.setActivityTypes}
            onVibrationModesChange={controller.setVibrationModes}
          />
        </CardBody>
      </Card>

      <SelectionSummary
        totalActivities={controller.selectedCount}
        totalPatients={controller.patientCount}
        onDownload={controller.handleDownload}
        isDownloading={isDownloading}
        progress={controller.downloadProgress}
      />

      {(controller.preview || controller.isLoadingPreview || controller.isLoadingActivities) && (
        <Card>
          <CardHeader
            title="Preview"
            description={
              controller.preview
                ? `${controller.preview.totalCount} activities from ${controller.preview.patientCount} patients`
                : 'Loading preview...'
            }
          />
          <CardBody>
            <SelectionPreview
              activities={controller.preview?.activities || []}
              totalCount={controller.preview?.totalCount || 0}
              isLoading={controller.isLoadingPreview || controller.isLoadingActivities}
              restrictUnavailableActivities={mode === 'processed'}
              selectedActivityIds={controller.selectedActivityIds}
              onToggleActivity={controller.toggleActivitySelection}
              onSelectAll={controller.selectAllActivities}
              onDeselectAll={controller.deselectAllActivities}
            />
          </CardBody>
        </Card>
      )}
    </div>
  );
}
